import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin, Phone } from "lucide-react";
import { scrollToId } from "../lib/smooth";

const HERO_IMG =
    "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?q=80&w=1920&auto=format&fit=crop";

const line = {
    hidden: { y: "112%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 0.95, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
    }),
};

const MaskedLine = ({ children, i, className = "" }) => (
    <span className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
        <motion.span
            className={`block ${className}`}
            variants={line}
            initial="hidden"
            animate="show"
            custom={i}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            className="relative min-h-screen flex flex-col justify-end overflow-hidden"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0" data-testid="hero-parallax-bg">
                <img
                    src={HERO_IMG}
                    alt="FRP manufacturing facility"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#09090b]/72" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/30 to-[#09090b]/80" />
            </motion.div>

            {/* vertical side meta */}
            <motion.div
                style={{ opacity: fade }}
                className="hidden xl:flex absolute right-16 top-1/2 -translate-y-1/2 items-center gap-4 [writing-mode:vertical-rl]"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-500">
                    Plot G-27, SIDA Industrial Area — Jaunpur, U.P. 222202 — India
                </span>
                <span className="w-px h-24 bg-zinc-700" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="shell relative z-10 pt-40 pb-14">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.6 }}
                    className="font-mono text-[11px] md:text-xs uppercase tracking-[0.3em] text-safety flex items-center gap-3 mb-8"
                >
                    <span className="w-10 h-px bg-safety inline-block" />
                    FRP • GFRP • Epoxy Fiberglass • Electrical Insulation
                </motion.p>

                <h1 className="font-display font-semibold uppercase leading-[0.94] tracking-tight text-zinc-50 text-[13.5vw] sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
                    <MaskedLine i={0}>Engineered</MaskedLine>
                    <MaskedLine i={1} className="text-safety">
                        FRP Solutions
                    </MaskedLine>
                    <MaskedLine i={2} className="text-outline">
                        For Electrical &amp;
                    </MaskedLine>
                    <MaskedLine i={3} className="text-outline">
                        Industrial Applications
                    </MaskedLine>
                </h1>

                <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.95, duration: 0.7 }}
                        className="max-w-xl text-zinc-300 text-sm md:text-base leading-relaxed"
                    >
                        Naagarjun Power Fiber manufactures FRP/GFRP sheets, epoxy
                        fiberglass components and customised FRP components for
                        electrical, transformer, industrial and engineering
                        applications — built to your approved drawings.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.1, duration: 0.7 }}
                        className="flex flex-wrap gap-4"
                    >
                        <a
                            href="#enquiry"
                            data-testid="hero-cta-enquiry"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId("#enquiry");
                            }}
                            className="btn-solid"
                        >
                            Send an Enquiry <ArrowUpRight size={16} />
                        </a>
                        <a
                            href="tel:+919648881601"
                            data-testid="hero-cta-call"
                            className="btn-ghost"
                        >
                            <Phone size={16} className="text-safety" />
                            Call +91 96488 81601
                        </a>
                        <a
                            href="#profile"
                            data-testid="hero-cta-capabilities"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId("#profile");
                            }}
                            className="btn-ghost hidden sm:inline-flex"
                        >
                            Explore Capabilities <ArrowDown size={16} />
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.3, duration: 0.8 }}
                    className="mt-14 border-t border-zinc-800 grid grid-cols-3"
                >
                    {[
                        { n: "10", l: "Core Capabilities" },
                        { n: "11", l: "Step Process Control" },
                        { n: "7", l: "Industries Served" },
                    ].map((s) => (
                        <div key={s.l} className="py-6 md:py-8 pr-4">
                            <div className="font-display text-3xl md:text-5xl font-semibold text-safety">
                                {s.n}
                            </div>
                            <div className="font-mono text-[9px] md:text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-2">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            <motion.div
                style={{ opacity: fade }}
                className="absolute bottom-8 right-6 hidden md:flex items-center gap-3 z-10"
            >
                <MapPin size={13} className="text-safety" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Jaunpur — India
                </span>
            </motion.div>
        </section>
    );
};

export default Hero;